import React, { useEffect, useState } from 'react';
import { Entity, PointGraphics } from "resium";
import { Cartesian3, Color, NearFarScalar, CallbackProperty, JulianDate, HeightReference } from "cesium";

// Define colors for different asset categories
const categoryColors = {
  'Road Pavement': '#e74c3c',    // Red
  'Structural': '#3498db',       // Blue
  'Geotechnical': '#9b59b6',    // Purple
  'Road Furniture': '#16a085',   // Teal
  'Vertical Asset': '#7c4dff',   // Deep Purple
  'default': '#95a5a6'           // Grey (default)
};

const EntityPointLayer = ({ assets, useRatingColors = false }) => {
  const [time, setTime] = useState(0);

  // Create a more rapid pulsation effect
  useEffect(() => {
    let frame;
    const animate = () => {
      setTime(prev => (prev + 0.05) % (Math.PI * 2)); // Increased increment for faster pulsing
      frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frame); // Cleanup on component unmount
    };
  }, []);

  // Create a pulsating point size using current time value
  const getPulsatingSize = (baseSize) => {
    return new CallbackProperty(() => {
      // Sine wave oscillation with increased amplitude for more noticeable effect
      return baseSize + 3 * Math.sin(time);
    }, false);
  };

  // Create a pulsating outline width
  const getPulsatingOutlineWidth = () => {
    return new CallbackProperty(() => {
      // Oscillate outline width with increased range
      return 2 + 0.8 * Math.sin(time);
    }, false);
  };

  // Get color based on asset - use rating color or category color
  const getAssetColor = (asset) => {
    if (useRatingColors) {
      // Use rating color when Geotechnical/Slope filter is active
      const ratingColor = asset.rating_color_code || '#9e9e9e'; // Grey for no rating
      return Color.fromCssColorString(ratingColor);
    } else {
      // Default: category-based color
      const categoryName = asset.category_name || 'default';
      const colorHex = categoryColors[categoryName] || categoryColors.default;
      return Color.fromCssColorString(colorHex);
    }
  };

  if (!assets || assets.length === 0) {
    return null; // Return null if there are no assets to render
  }

  return (
    <>
      {assets.map(asset => {
        // Check for valid coordinates before rendering
        if (typeof asset.center_lat !== 'number' || typeof asset.center_lng !== 'number') {
          return null; // Skip assets with invalid coordinates
        }
        
        // Get color based on asset category
        const assetColor = getAssetColor(asset);
        
        return (
          <Entity
            key={asset.id} // Unique key for React list rendering
            id={`asset-${asset.id}`} // Ensure the ID matches what the interaction handler expects
            position={Cartesian3.fromDegrees(asset.center_lng, asset.center_lat)} // No height specified
            name={asset.name || `Asset ${asset.id}`}
            properties={{ 
              assetData: asset,
              // Add more detailed properties for tooltip/info display
              assetType: asset.subtype_name || 'Unknown',
              assetCategory: asset.category_name || 'Unknown',
              assetNetwork: asset.network_name || 'Unknown'
            }}
          >
            <PointGraphics 
              pixelSize={getPulsatingSize(12)}
              color={assetColor}
              outlineColor={Color.WHITE}
              outlineWidth={getPulsatingOutlineWidth()}
              scaleByDistance={new NearFarScalar(1000, 1.0, 500000, 0.6)}
              translucencyByDistance={new NearFarScalar(1000, 1.0, 500000, 0.7)}
              heightReference={HeightReference.NONE} // No terrain clamping for better performance
              disableDepthTestDistance={Number.POSITIVE_INFINITY} // Always show on top
            />
          </Entity>
        );
      })}
    </>
  );
};

export default EntityPointLayer;